/**
 * `SHELL-040`/`SHELL-041`. Goals and their live progress, the move and time
 * constraints where the level defines them, and the pause/restart/quit
 * controls.
 *
 * Reads `progress`/`remainingMs` as `Signal`s, not plain props, and reads
 * their `.value` here, inside this component's own render -- `@preact/signals`
 * (ADR-0005) subscribes whichever component actually reads `.value` during
 * render, so a tick of either one re-renders only this component, never
 * `Play.tsx` or the game surface it hosts (`SHELL-045`). `Play.tsx` holds the
 * `Signal` objects and never reads `.value` itself for exactly this reason.
 *
 * Deliberately game-agnostic in what it can label. `collect`/`clear-obstacle`
 * goals carry only a game-defined token (`goal.piece`/`goal.obstacle`) with
 * no display name anywhere in `@contracts` -- match-3's own `pieceName()`
 * (`cellLabel.ts`) is `ui/`-private and this file must not import it
 * (CLAUDE.md rule 3/4). The raw token is what renders. Honest and unpolished
 * beats inventing a prettification scheme this stage has no second game to
 * check the shape of.
 */

import type { Signal } from '@preact/signals';

import type { GameProgress } from '@contracts/game-module';
import type { Goal } from '@contracts/level';
import { goalKey } from '@contracts/level';

export interface HudProps {
  goals: readonly Goal[];
  moveLimit: number;
  progress: Signal<GameProgress | null>;
  /** `null` where the level defines no time limit -- SHELL-040's own "any move or time constraint" is conditional on one existing. */
  remainingMs: Signal<number | null>;
  onPause: () => void;
  onRestart: () => void;
  onQuit: () => void;
}

function goalLabel(goal: Goal): string {
  switch (goal.kind) {
    case 'score':
      return 'Score';
    case 'collect':
    case 'avoid':
      return goal.piece;
    case 'clear-obstacle':
      return goal.obstacle;
    case 'custom':
      return goal.label;
    case 'time':
      return 'Time';
  }
}

function goalTarget(goal: Goal): number {
  switch (goal.kind) {
    case 'score':
      return goal.target;
    case 'collect':
    case 'clear-obstacle':
      return goal.count;
    case 'custom':
      return goal.target;
    case 'avoid':
      return goal.limit;
    case 'time':
      return goal.limitMs;
  }
}

/** `M:SS`, floored at zero -- `remainingMs` can read briefly negative between a timer tick and `onTimeExpired` actually landing. */
export function formatRemaining(ms: number): string {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes)}:${String(seconds).padStart(2, '0')}`;
}

export function Hud({
  goals,
  moveLimit,
  progress,
  remainingMs,
  onPause,
  onRestart,
  onQuit,
}: HudProps) {
  const live = progress.value;
  const remaining = remainingMs.value;

  return (
    <div class="hud">
      <ul class="hud__goals">
        {goals.map((goal) => {
          const key = goalKey(goal);
          const target = goalTarget(goal);
          const current = Math.min(live?.goalProgress[key] ?? 0, target);
          return (
            <li key={key} class="hud__goal">
              {goalLabel(goal)}: {current} / {target}
            </li>
          );
        })}
      </ul>
      <div class="hud__stats">
        {moveLimit > 0 && (
          <span class="hud__stat">
            Moves: {live?.movesUsed ?? 0} / {moveLimit}
          </span>
        )}
        {remaining !== null && <span class="hud__stat">Time: {formatRemaining(remaining)}</span>}
      </div>
      <div class="hud__controls">
        <button type="button" onClick={onPause}>
          Pause
        </button>
        <button type="button" onClick={onRestart}>
          Restart
        </button>
        <button type="button" onClick={onQuit}>
          Quit
        </button>
      </div>
    </div>
  );
}
